import { DailySnapshot, HourlySnapshot } from '@/core/tokens';
import { useNetwork } from '@/presentation/context/network';
import { Address } from 'nftx.js';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useFormatter } from 'use-intl';
import VaultIcon from '@/components/elements/VaultIcon';
import { buildDataForChart } from './utils';
import { useMemo } from 'react';

const COLORS = ['#6A24FD', '#FD247F', '#24C9FD'];

const MultiPriceChart = ({
  snapshots,
}: {
  snapshots: {
    vaultId: string;
    tokenAddress: Address;
    hourlySnapshots: HourlySnapshot[];
    dailySnapshots: DailySnapshot[];
  }[];
}) => {
  const { network } = useNetwork();
  const { dateTime, number } = useFormatter();

  const data = useMemo(() => {
    const rawData = buildDataForChart(snapshots, network);

    // Process rawData to exclude outliers (both spikes and drops)
    const processedData = rawData.map((dataset) => {
      const result = [];
      let prevPrice = null;

      for (let i = 0; i < dataset.length; i++) {
        const point = dataset[i];
        const currPrice = point.price;

        if (currPrice <= 0) {
          // Skip invalid price data
          continue;
        }

        if (prevPrice === null) {
          // Include the first data point unconditionally and set prevPrice
          result.push(point);
          prevPrice = currPrice;
          continue;
        }

        const changeRatio = currPrice / prevPrice;

        // Optional logging for debugging
        console.log(
          `Index: ${i}, CurrPrice: ${currPrice}, PrevPrice: ${prevPrice}, ChangeRatio: ${changeRatio}`
        );

        // Adjusted thresholds for outlier detection
        if (changeRatio > 20 || changeRatio < 0.07) {
          // Skip this data point
          continue;
        } else {
          // Include the data point and update prevPrice
          result.push(point);
          prevPrice = currPrice;
        }
      }

      return result;
    });

    return processedData;
  }, [network, snapshots]);

  return (
    <ResponsiveContainer width={'100%'} minHeight={300} height={'100%'}>
      <LineChart>
        {data.map((x, i) => (
          <Line
            data={x}
            key={i}
            dataKey={'price'}
            type="monotone"
            stroke={COLORS[i]}
            strokeWidth={2}
            dot={false}
          />
        ))}
        <YAxis
          padding={{ top: 10 }}
          fontSize={10}
          axisLine={false}
          width={30}
          tickMargin={10}
          tickSize={0}
        />
        <XAxis
          axisLine={false}
          dataKey={'day'}
          type="number"
          tickFormatter={(val) =>
            dateTime(val * 60 * 60 * 24 * 1000, {
              dateStyle: 'medium',
            })
          }
          domain={['dataMin', 'dataMax']}
          fontSize={10}
          minTickGap={64}
          tickSize={0}
          tickMargin={16}
        />
        <Tooltip
          offset={0}
          cursor={false}
          content={({ payload }) => {
            // No active hover point data
            if (!payload?.length) return null;
            // Weird issue where the space between data points fills with the same values
            if (
              payload.length > 1 &&
              payload.every((x) => x.value === payload[0].value)
            )
              return null;

            return (
              <div className="rounded-md border border-gray-300 p-3 text-sm bg-gray-50 space-y-2">
                <p className="text-xs text-gray-500 font-medium">
                  {dateTime(payload?.[0]?.payload?.day * 60 * 60 * 24 * 1000, {
                    year: 'numeric',
                    day: 'numeric',
                    month: 'long',
                    hour: 'numeric',
                    minute: 'numeric',
                  })}
                </p>
                {payload?.map((p, i) => (
                  <div key={i} className="flex items-center">
                    <VaultIcon
                      vaultId={p?.payload.vaultId}
                      className="w-8 mr-2"
                    />
                    <b>
                      {number(p?.payload.price, {
                        minimumSignificantDigits: 2,
                        maximumSignificantDigits: 6,
                      })}
                      {' ETH'}
                    </b>
                  </div>
                ))}
              </div>
            );
          }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MultiPriceChart;
